import type {
  ApiKeyDtoT,
  ApiRequestLogDtoT,
  CreateApiKeyInputT,
  CreateApiKeyResultT,
  UpdateApiKeyInputT,
  UpdateApiKeyResultT,
  WebhookDeliveryDtoT,
} from '@wave/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

// Хуки для «Публичный API приёма заявок» — вкладка «Ключи» /admin/api и (задача 10)
// вкладка «Журнал». Паттерн — как api/users.ts: локальный KEY + invalidate onSuccess.

const KEY = ['integration', 'keys'] as const;

export function useApiKeys() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api.get<ApiKeyDtoT[]>('/integration/keys'),
  });
}

export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateApiKeyInputT) =>
      api.post<CreateApiKeyResultT>('/integration/keys', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; input: UpdateApiKeyInputT }) =>
      api.patch<UpdateApiKeyResultT>(`/integration/keys/${vars.id}`, vars.input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useRevokeApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<ApiKeyDtoT>(`/integration/keys/${id}/revoke`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useRegenerateWebhookSecret() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<UpdateApiKeyResultT>(`/integration/keys/${id}/webhook-secret`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Журнал запросов публичного API (вкладка «Журнал», задача 10). */
export function useApiRequests(params: { keyId?: string; limit?: number }) {
  return useQuery({
    queryKey: ['integration', 'requests', params.keyId ?? 'all', params.limit ?? 'default'] as const,
    queryFn: () =>
      api.get<ApiRequestLogDtoT[]>('/integration/requests', {
        ...(params.keyId ? { keyId: params.keyId } : {}),
        ...(params.limit ? { limit: params.limit } : {}),
      }),
  });
}

/** Журнал доставки вебхуков (вкладка «Журнал», задача 10). Эндпоинт — задача 7. */
export function useWebhookDeliveries(params: { keyId?: string; status?: string }) {
  return useQuery({
    queryKey: ['integration', 'deliveries', params.keyId ?? 'all', params.status ?? 'all'] as const,
    queryFn: () =>
      api.get<WebhookDeliveryDtoT[]>('/integration/deliveries', {
        ...(params.keyId ? { keyId: params.keyId } : {}),
        ...(params.status ? { status: params.status } : {}),
      }),
  });
}

export function useRetryDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<WebhookDeliveryDtoT>(`/integration/deliveries/${id}/retry`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integration', 'deliveries'] }),
  });
}
